import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { Icon } from '@/components/Icon'
import {
  createView,
  updateView,
  FILTER_FIELDS,
  FILTER_OPERATORS,
  type FilterNode,
  type SavedView,
  type ViewLayout,
} from '@/features/views/api'
import type { ApiError } from '@/lib/api/client'

/** One row of the editor. The tree it builds is always a flat `and`. */
interface Condition {
  field: string
  operator: string
  value: string
}

/** Operators that take no value — the value box disappears for these. */
const VALUELESS = ['is_null', 'is_not_null']

function toConditions(filter: FilterNode | undefined): Condition[] {
  if (filter === undefined) return []

  const leaves = 'and' in filter ? filter.and : [filter]

  return leaves.flatMap((node): Condition[] =>
    'field' in node
      ? [{
          field: node.field,
          operator: node.operator,
          value: node.value === undefined || node.value === null ? '' : String(node.value),
        }]
      : [],
  )
}

function toFilter(conditions: Condition[]): FilterNode | undefined {
  const leaves = conditions
    .filter((condition) => condition.field !== '')
    .map((condition): FilterNode =>
      VALUELESS.includes(condition.operator)
        ? { field: condition.field, operator: condition.operator }
        : {
            field: condition.field,
            operator: condition.operator,
            // `in` and `not_in` take a list; everything else takes one value.
            value: ['in', 'not_in'].includes(condition.operator)
              ? condition.value.split(',').map((part) => part.trim()).filter(Boolean)
              : condition.value,
          },
    )

  return leaves.length === 0 ? undefined : { and: leaves }
}

/**
 * Naming a filter so it can be come back to.
 *
 * Conditions are joined with `and` only. The server's grammar nests `or` and
 * groups arbitrarily deep, and a builder for that is a query language with a
 * mouse — this covers what a saved view is actually used for, and a filter
 * needing more than this is better written against the API.
 */
export function SaveViewDialog({
  projectKey,
  projectId,
  layout,
  existing,
  onClose,
  onSaved,
}: {
  projectKey: string
  projectId: string
  layout: ViewLayout
  existing?: SavedView
  onClose: () => void
  onSaved: (view: SavedView) => void
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [name, setName] = useState(existing?.name ?? '')
  const [shared, setShared] = useState(existing?.isShared ?? false)
  const [conditions, setConditions] = useState<Condition[]>(
    toConditions(existing?.definition?.filter),
  )

  const save = useMutation<SavedView, ApiError, void>({
    mutationFn: () => {
      const filter = toFilter(conditions)
      const definition = filter === undefined ? {} : { filter }

      return existing === undefined
        ? createView({
            name: name.trim(),
            layout,
            projectId,
            isShared: shared,
            definition,
          })
        : updateView(existing.id, { name: name.trim(), isShared: shared, definition, layout })
    },
    onSuccess: (view) => {
      void queryClient.invalidateQueries({ queryKey: ['views', projectKey] })
      onSaved(view)
      onClose()
    },
  })

  const setRow = (index: number, patch: Partial<Condition>) =>
    setConditions((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  return (
    <Dialog title={existing === undefined ? t('views.save') : t('views.rename')} onClose={onClose}>
      <form
        className="flex max-h-[70vh] flex-col gap-3.5 overflow-y-auto px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() !== '') save.mutate()
        }}
      >
        <Field label={t('views.name')}>
          <input
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        <div className="flex flex-col gap-2">
          <span className="text-ink-500 font-mono text-[10px] tracking-widest uppercase">
            {t('views.filters')}
          </span>

          {conditions.map((condition, index) => (
            <div key={index} className="flex items-center gap-1.5">
              <select
                value={condition.field}
                aria-label={t('views.field')}
                onChange={(event) => setRow(index, { field: event.target.value })}
                className={`${FIELD_CLASS} flex-1`}
              >
                {FILTER_FIELDS.map((field) => (
                  <option key={field} value={field}>
                    {t(`views.fieldName.${field}`)}
                  </option>
                ))}
              </select>

              <select
                value={condition.operator}
                aria-label={t('views.operator')}
                onChange={(event) => setRow(index, { operator: event.target.value })}
                className={`${FIELD_CLASS} w-32 shrink-0`}
              >
                {FILTER_OPERATORS.map((operator) => (
                  <option key={operator} value={operator}>
                    {t(`views.operatorName.${operator}`)}
                  </option>
                ))}
              </select>

              {VALUELESS.includes(condition.operator) ? null : (
                <input
                  value={condition.value}
                  aria-label={t('views.value')}
                  onChange={(event) => setRow(index, { value: event.target.value })}
                  className={`${FIELD_CLASS} flex-1`}
                />
              )}

              <button
                type="button"
                onClick={() => setConditions((rows) => rows.filter((_, i) => i !== index))}
                title={t('views.remove')}
                aria-label={t('views.remove')}
                className="text-ink-500 hover:text-[color:var(--color-state-blocked)] shrink-0 rounded p-1.5"
              >
                <Icon name="close" className="size-3.5" />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() =>
              setConditions((rows) => [...rows, { field: 'state_category', operator: 'eq', value: '' }])
            }
            className="text-brand-600 flex items-center gap-1 self-start text-[12px]"
          >
            <Icon name="plus" className="size-3" />
            {t('views.addFilter')}
          </button>
        </div>

        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={shared}
            onChange={(event) => setShared(event.target.checked)}
          />
          <span className="text-ink-700">{t('views.shared')}</span>
        </label>

        <p className="text-ink-500 text-[11px] leading-tight">{t('views.explain')}</p>

        {save.isError ? (
          <p className="text-[12px] text-[color:var(--color-state-blocked)]">{save.error.message}</p>
        ) : null}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={save.isPending || name.trim() === ''}
            className="bg-brand-600 rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
          >
            {save.isPending ? t('views.creating') : t('views.create')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
